<template>
  <Page
    v-if="!loading"
    title="Manage Communications"
    :subpage="subpage"
    :warning-message="warningMessage"
  >
    <template #actions>
      <div class="flex flex-col gap-3 md:flex-row md:items-center">
        <ButtonToggle
          v-if="showCategories"
          v-model="currentCategoryId"
          :options="buttonOptions"
        />
        <Button
          text="View Change History"
          icon="fa-regular fa-clock-rotate-left"
          @click="$refs.flyoutPanel.show()"
        />
      </div>
    </template>
    <component :is="wrapperComponent" headless>
      <CategorySetting
        v-for="categorySetting in settings.categories"
        :key="settingKey(categorySetting)"
        :category-setting="categorySetting"
        :communication-settings="settings"
        :class="{ 'd-none': currentCategory !== categorySetting }"
        @change="updateChanges"
      />
      <div class="communications">
        <CommunicationSetting
          v-for="communicationSetting in communications"
          :key="settingKey(communicationSetting)"
          :communication-setting="communicationSetting"
          :communication-settings="settings"
          @change="updateChanges"
        />
      </div>
      <div class="save-changes">
        <div>Save Changes to Communications When You're Done.</div>
        <sera-button
          label="Save Changes"
          :disabled="!changed"
          @click="showConfirmation"
        />
      </div>
    </component>
    <ConfirmationModal
      v-if="confirmationOpen"
      @hide-modal="hideConfirmation"
      @save="saveChanges"
    />
    <FlyoutPanel ref="flyoutPanel" title="Communications Change History">
      <ChangeHistoryDatatable
        ref="changeHistoryDatatable"
        :source="changeHistoryPath"
      />
    </FlyoutPanel>
  </Page>
</template>

<script setup lang="ts">
import API from "serviceshift-ui/api-client";
import { computed, onMounted, onUnmounted, ref, watch } from "vue";
import CommunicationSettings from "@/lib/communication_settings";
import { authenticityToken } from "@/lib/helpers";

import Button from "@/components/Button.vue";
import ButtonToggle from "@/components/ButtonToggle.vue";
import Card from "@/components/Card.vue";
import FlyoutPanel from "@/components/FlyoutPanel.vue";
import Page from "@/components/Page.vue";
import CategorySetting from "@/Communications/CategorySetting.vue";
import ChangeHistoryDatatable from "@/Communications/ChangeHistoryDatatable.vue";
import CommunicationSetting from "@/Communications/CommunicationSetting.vue";
import ConfirmationModal from "@/Communications/ConfirmationModal.vue";

const props = defineProps({
  settingsPath: {
    type: String,
    required: true
  },
  changeHistoryPath: {
    type: String,
    required: true
  },
  subpage: {
    type: Boolean,
    default: false
  }
});

const confirmationOpen = ref(false);

onMounted(() => fetchData());
onMounted(() => window.addEventListener("beforeunload", displayWarning));
onUnmounted(() => window.removeEventListener("beforeunload", displayWarning));

const loading = ref(true);
const settings = ref({});
const currentCategoryId = ref(null);
const changeHistoryDatatable = ref(null);
const changed = computed(() => settings.value.isUpdated);
const wrapperComponent = computed(() => (props.subpage ? "div" : Card));
const showCategories = computed(() => settings.value.categories.length > 1);
const warningMessage = computed(() => {
  const category = currentCategory.value?.category;
  if (!settings.value.parentSettings?.anyDisabled(category)) return "";

  return "Note: Some settings have been turned off at the account level.";
});

const currentCategory = computed(() => {
  return settings.value.categories.find(
    (cat) => cat.category === currentCategoryId.value
  );
});

async function fetchData() {
  const response = await API.makeRequest(props.settingsPath);
  initialize(response.data);
  loading.value = false;
}

function initialize(settingsData) {
  settings.value = new CommunicationSettings(settingsData);
  let category;
  if (currentCategory.value) {
    category = currentCategory.value.category;
  } else {
    category = settings.value.categories[0].category;
  }
  currentCategoryId.value = settings.value.findSetting({ category }).category;
}

const communications = computed(() => {
  if (!currentCategory.value) return null;
  return settings.value.findCommunications(currentCategory.value.category);
});

const buttonOptions = computed(() => {
  console.log(settings.value.categories);
  return settings.value.categories.map((cat) => ({
    label: cat.name,
    value: cat.category
  }));
});

function settingKey(setting) {
  return setting.key ? `${setting.category}_${setting.key}` : setting.category;
}

function updateChanges(newSetting) {
  settings.value.update(newSetting);
}

async function saveChanges(reason) {
  const data = {
    updates: settings.value.changes,
    reason
  };
  const response = await API.makeRequest(props.settingsPath, {
    method: "PATCH",
    data,
    headers: {
      "X-CSRF-Token": authenticityToken()
    }
  });
  initialize(response.data);
  confirmationOpen.value = false;
  window.notify.success("Setting saved successfully!");
  changeHistoryDatatable.value.refresh();
}

function showConfirmation() {
  confirmationOpen.value = true;
}

function hideConfirmation() {
  confirmationOpen.value = false;
}

function displayWarning(e) {
  if (settingsChanged()) {
    e.preventDefault();
    return (e.returnValue = "Changes you made may not be saved.");
  }
}

function settingsChanged() {
  return changed.value;
}

defineExpose({ settingsChanged });
</script>

<style lang="scss" scoped>
.communications {
  padding: 10px;
  margin: 20px -10px 0px;
  padding-right: 6px;
}
.save-changes {
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-radius: 4px;
  background: var(--light-gray);
  padding: 20px 16px;
  margin-top: 24px;
  font-weight: 700;
}
</style>
